import { useNotificationsOptional } from '@/contexts/NotificationsContext';
import type { Notification } from '@/types/notification';
import type { User } from '@/types/user';
import { profilesService } from '@/lib/supabase/profiles';
import { FiBell, FiMessageCircle, FiUsers } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useMemo } from 'react';
import { logError } from '@/utils/error-handling';
import './NotificationBell.css';

interface NotificationBellProps {
  variant?: 'desktop' | 'mobile';
}

const getNotificationDestination = (notification: Notification) => {
  if (notification.type === 'chat_message') {
    const senderId = notification.payload?.sender_id;
    if (senderId) {
      return `/chats?tab=chats&user=${senderId}`;
    }
    return '/chats?tab=chats';
  }
  if (notification.type === 'admin_message') {
    return '/eventos';
  }
  if (notification.type === 'new_connection') {
    return '/chats?tab=actividad';
  }
  if (notification.type === 'event_notification' && notification.payload?.event_id) {
    return `/evento/${notification.payload.event_id}?tab=notificaciones`;
  }
  return '/eventos';
};

const getNotificationTitle = (notification: Notification, senderProfiles?: Map<string, User>) => {
  if (notification.type === 'admin_message') {
    return notification.payload?.title || 'Notificación del administrador';
  }
  if (notification.type === 'event_notification') {
    return notification.payload?.title || 'Notificación del evento';
  }
  if (notification.type === 'chat_message') {
    const senderId = notification.payload?.sender_id;
    if (senderId && senderProfiles) {
      const sender = senderProfiles.get(senderId);
      if (sender) {
        const senderName = sender.name || sender.firstName || 'Alguien';
        return `${senderName} te ha enviado un mensaje`;
      }
    }
    return 'Alguien te ha enviado un mensaje';
  }
  if (notification.type === 'new_connection') {
    const senderId = notification.payload?.sender_id;
    if (senderId && senderProfiles) {
      const sender = senderProfiles.get(senderId);
      if (sender) {
        const senderName = sender.name || sender.firstName || 'Alguien';
        return `${senderName} quiere conectar contigo`;
      }
    }
    return 'Alguien quiere conectar contigo';
  }
  return 'Nueva conexión';
};

const getNotificationIcon = (notification: Notification) => {
  if (notification.type === 'chat_message') {
    return FiMessageCircle;
  }
  if (notification.type === 'admin_message' || notification.type === 'event_notification') {
    return FiBell;
  }
  return FiUsers;
};

const formatNotificationTime = (dateString: string) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Ahora';
  if (diffMins < 60) return `Hace ${diffMins} min`;
  if (diffHours < 24) return `Hace ${diffHours} h`;
  if (diffDays < 7) return `Hace ${diffDays} d`;

  return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
};

export function NotificationBell({ variant = 'desktop' }: NotificationBellProps) {
  const [open, setOpen] = useState(false);
  const [pushMessage, setPushMessage] = useState<string | null>(null);
  const [senderProfiles, setSenderProfiles] = useState<Map<string, User>>(new Map());
  const navigate = useNavigate();
  const notificationsContext = useNotificationsOptional();

  const notifications = notificationsContext?.notifications ?? [];
  const unreadCount = notificationsContext?.unreadCount ?? 0;
  const markAsRead = notificationsContext?.markAsRead;
  const markAllAsRead = notificationsContext?.markAllAsRead;
  const isLoading = notificationsContext?.isLoading ?? false;
  const requestPushPermission = notificationsContext?.requestPushPermission;

  // Hooks siempre en el mismo orden (nunca después de un return condicional)
  const senderIds = useMemo(() => {
    const ids = new Set<string>();
    notifications.forEach((notification) => {
      if (
        (notification.type === 'chat_message' || notification.type === 'new_connection') &&
        notification.payload?.sender_id
      ) {
        ids.add(notification.payload.sender_id);
      }
    });
    return Array.from(ids);
  }, [notifications]);

  useEffect(() => {
    if (!open || senderIds.length === 0) {
      return;
    }

    let isMounted = true;

    const loadProfiles = async () => {
      try {
        const users = await profilesService.getUsersByIds(senderIds);
        if (!isMounted) return;

        const profilesMap = new Map<string, User>();
        users.forEach((user) => {
          profilesMap.set(user.id, user);
        });

        setSenderProfiles(profilesMap);
      } catch (error) {
        logError(error, 'NotificationBell.loadProfiles', true);
      }
    };

    loadProfiles();

    return () => {
      isMounted = false;
    };
  }, [open, senderIds]);

  const containerClasses = [
    'notification-bell',
    variant === 'mobile' ? 'notification-bell--mobile' : 'notification-bell--desktop',
  ].join(' ');

  if (!notificationsContext) {
    return (
      <div className={containerClasses}>
        <button
          className="notification-bell-button"
          onClick={() => {}}
          aria-label="Notificaciones (Error)"
          title="Ejecuta las migraciones SQL"
          type="button"
        >
          <FiBell size={variant === 'mobile' ? 24 : 20} />
        </button>
      </div>
    );
  }

  const toggle = () => setOpen((prev) => !prev);

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.read_at) {
      void markAsRead?.(notification.id);
    }
    navigate(getNotificationDestination(notification));
    setOpen(false);
  };

  const canEnablePush =
    typeof window !== 'undefined' &&
    'Notification' in window &&
    Notification.permission !== 'granted';

  const handleEnablePush = async () => {
    if (!requestPushPermission) return;
    const result = await requestPushPermission();
    if (result.success) {
      setPushMessage('Notificaciones push activadas.');
    } else {
      setPushMessage('No se pudieron activar las push.');
    }
  };

  const dropdownClasses = [
    'notification-bell-dropdown',
    variant === 'mobile' ? 'notification-bell-dropdown--mobile' : 'notification-bell-dropdown--desktop',
  ].join(' ');
  const overlayClasses = [
    'notification-bell-overlay',
    variant === 'mobile' ? 'notification-bell-overlay--mobile' : 'notification-bell-overlay--desktop',
  ].join(' ');

  return (
    <div className={containerClasses}>
      <button
        className="notification-bell-button"
        onClick={toggle}
        aria-label="Notificaciones"
        disabled={isLoading}
        type="button"
      >
        <FiBell size={variant === 'mobile' ? 24 : 20} />
        {unreadCount > 0 && <span className="notification-bell-badge">{unreadCount}</span>}
      </button>
      {open && (
        <>
          <div
            className={overlayClasses}
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
          <div className={dropdownClasses}>
            <div className="notification-bell-dropdown-header">
              <span>Notificaciones</span>
              {notifications.length > 0 && (
                <button onClick={() => void markAllAsRead?.()} type="button">
                  Marcar todas como leídas
                </button>
              )}
            </div>
            {notifications.length === 0 ? (
              <div className="notification-bell-empty">
                <div className="notification-bell-empty-icon">🔔</div>
                <div className="notification-bell-empty-text">No tienes notificaciones</div>
              </div>
            ) : (
              <ul>
                {notifications.map((notification) => {
                  const Icon = getNotificationIcon(notification);
                  const isUnread = !notification.read_at;

                  return (
                    <li
                      key={notification.id}
                      className={`notification-bell-item ${isUnread ? 'notification-bell-item--unread' : ''}`}
                    >
                      <button
                        onClick={() => handleNotificationClick(notification)}
                        type="button"
                        className="notification-bell-item-button"
                      >
                        <div className="notification-bell-item-icon">
                          <Icon size={18} />
                        </div>
                        <div className="notification-bell-item-content">
                          <div className="notification-bell-item-title">
                            {getNotificationTitle(notification, senderProfiles)}
                          </div>
                          <div className="notification-bell-item-time">
                            {formatNotificationTime(notification.created_at)}
                          </div>
                        </div>
                        {isUnread && <div className="notification-bell-item-dot" />}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
            {canEnablePush && (
              <button className="notification-bell-push-button" onClick={handleEnablePush} type="button">
                Activar notificaciones push
              </button>
            )}
            {pushMessage && <div className="notification-bell-push-message">{pushMessage}</div>}
          </div>
        </>
      )}
    </div>
  );
}
